"use client";

import React, { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type SectionLanguage = "en" | "it";
export default function CeremonyReception() {
  const [language, setLanguage] = useState<SectionLanguage>("en");
  const {
    ref: ceremonyRef,
    isVisible: ceremonyVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: receptionRef,
    isVisible: receptionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  useEffect(() => {
    const savedLanguage = localStorage.getItem("site-language");
    if (savedLanguage === "en" || savedLanguage === "it") {
      setLanguage(savedLanguage);
    }
  }, []);
  useEffect(() => {
    const handleStorageChange = () => {
      const savedLanguage = localStorage.getItem("site-language");
      if (savedLanguage === "en" || savedLanguage === "it") {
        setLanguage(savedLanguage);
      }
    };
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("focus", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("focus", handleStorageChange);
    };
  }, []);
  const t = {
    en: {
      ceremonyTitle: "The Ceremony",
      ceremonyLocation: "Chiesa di Santo Spirito in Sassia",
      ceremonyTime: "4:00 PM",
      ceremonyButton: "View Location",
      ceremonyButtonAria: "View ceremony location on map",
      ceremonyDescription: "Join us for a Catholic Mass in the Church of Santo Spirito in Sassia: a 16th-century sanctuary steps from St. Peter's, and the very church where Sara's parents were married.",
      receptionTitle: "The Reception",
      receptionLocation: "Rome Cavalieri Waldorf Astoria",
      receptionTime: "Arrival at 6:00 PM",
      receptionSchedule: "Aperitivo · Dinner · After Party",
      receptionButton: "View Location",
      receptionButtonAria: "View reception location on map",
      receptionDescription: "After the ceremony, we invite you to join us at the Rome Cavalieri Waldorf Astoria for the reception. We will begin with drinks and appetizers, followed by dinner, and continue the evening with music and dancing into the night.",
      ceremonyAlt: "Gold illustration of Basilica di Santa Sabina",
      receptionAlt: "Elegant reception at a Roman Villa"
    },
    it: {
      ceremonyTitle: "La Cerimonia",
      ceremonyLocation: "Chiesa di Santo Spirito in Sassia",
      ceremonyTime: "16:00",
      ceremonyButton: "Vedi Location",
      ceremonyButtonAria: "Visualizza la location della cerimonia sulla mappa",
      ceremonyDescription: "Unitevi a noi per una Santa Messa nella Chiesa di Santo Spirito in Sassia, lo stesso luogo in cui i genitori di Sara si sono sposati.",
      receptionTitle: "La Reception",
      receptionLocation: "Rome Cavalieri Waldorf Astoria",
      receptionTime: "Arrivo alle 18:00",
      receptionSchedule: "Aperitivo · Cena · After Party",
      receptionButton: "Vedi Location",
      receptionButtonAria: "Visualizza la location della reception sulla mappa",
      receptionDescription: "Dopo la cerimonia, vi aspettiamo al Rome Cavalieri Waldorf Astoria per la reception. Inizieremo con un aperitivo, seguito dalla cena, e continueremo la serata con musica e balli fino a tardi.",
      ceremonyAlt: "Illustrazione dorata della Basilica di Santa Sabina",
      receptionAlt: "Elegante ricevimento in una villa romana"
    }
  }[language];
  return <section id="ceremony-reception" className="bg-background py-20 md:py-28 lg:py-40 overflow-hidden relative">
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_top_right,var(--color-accent)_0%,transparent_40%)] opacity-20" />
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_bottom_left,var(--color-secondary)_0%,transparent_40%)] opacity-20" />

      <div className="container mx-auto px-5 sm:px-6 md:px-12 max-w-7xl relative z-10">
        {/* Ceremony */}
        <div ref={ceremonyRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 lg:gap-8 items-center mb-24 md:mb-32 lg:mb-48 transition-all duration-1000 ease-out ${ceremonyVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <div className="lg:col-span-5 lg:col-start-2 flex flex-col justify-center">
            <div className="order-1">
              <div className="mb-6 md:mb-8">
                <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-foreground mb-5 md:mb-6 tracking-tight">
                  {t.ceremonyTitle}
                </h2>

                <div className="flex flex-col gap-3 text-primary font-medium tracking-widest uppercase text-sm">
                  <span className="flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 shrink-0" aria-hidden="true">
                      <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"></path>
                      <circle cx="12" cy="10" r="3"></circle>
                    </svg>
                    <span>{t.ceremonyLocation}</span>
                  </span>

                  <span className="flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 shrink-0" aria-hidden="true">
                      <path d="M12 6v6l4 2"></path>
                      <circle cx="12" cy="12" r="10"></circle>
                    </svg>
                    <span>{t.ceremonyTime}</span>
                  </span>
                </div>

                <div className="mt-5 md:mt-6">
                  <Button asChild variant="outline" className="rounded-none border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors duration-300">
                    <a href="https://maps.app.goo.gl/urnEaLGbwifGfh5A9" target="_blank" rel="noopener noreferrer" aria-label={t.ceremonyButtonAria}>
                      {t.ceremonyButton}
                    </a>
                  </Button>
                </div>
              </div>
            </div>

            <div className="order-2 lg:hidden mb-8">
              <div className="aspect-[3/4] relative w-full max-w-[280px] sm:max-w-[340px] mx-auto">
                <img src="https://saraandmichael2026.com/wp-content/uploads/2026/04/Chiesa-di-Santo-Spirito-in-Sassia.png" alt={t.ceremonyAlt} className="w-full h-full object-cover object-center relative z-10 shadow-xl shadow-shadow-color/10" />
              </div>
            </div>

            <div className="order-3 prose prose-lg max-w-none">
              <p className="text-muted-foreground text-left md:text-justify leading-loose border-l border-border/50 pl-4 md:pl-6 lg:border-none lg:pl-0">
                {t.ceremonyDescription}
              </p>
            </div>
          </div>

          <div className="hidden lg:block lg:col-span-5 lg:col-start-8 relative">
            <div className="aspect-[3/4] relative w-full max-w-md mx-auto lg:max-w-none">
              <div className="absolute -inset-4 border border-primary/20 z-0 hidden md:block" />
              <img src="https://saraandmichael2026.com/wp-content/uploads/2026/04/Chiesa-di-Santo-Spirito-in-Sassia.png" alt={t.ceremonyAlt} className="w-full h-full object-cover object-center relative z-10 shadow-xl shadow-shadow-color/10" />
            </div>
          </div>
        </div>

        {/* Reception */}
        <div ref={receptionRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 lg:gap-8 items-center transition-all duration-1000 ease-out delay-200 ${receptionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <div className="lg:col-span-6 lg:col-start-1 relative hidden lg:block">
            <div className="aspect-[4/5] relative w-full max-w-lg mx-auto lg:max-w-none">
              <div className="absolute -inset-4 border border-secondary z-0 hidden md:block translate-x-4 translate-y-4" />
              <img src="https://saraandmichael2026.com/wp-content/uploads/2026/04/ChatGPT-Image-Apr-20-2026-08_24_46-PM.png" alt={t.receptionAlt} className="w-full h-full object-cover object-center relative z-10 shadow-2xl shadow-shadow-color/20" />
            </div>
          </div>

          <div className="lg:col-span-5 lg:col-start-8 flex flex-col justify-center">
            <div className="order-1">
              <div className="mb-6 md:mb-8">
                <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-foreground mb-5 md:mb-6 tracking-tight">
                  {t.receptionTitle}
                </h2>

                <div className="flex flex-col gap-4 mb-0">
                  <div className="flex flex-col gap-3 text-primary font-medium tracking-widest uppercase text-sm">
                    <span className="flex items-center gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 shrink-0" aria-hidden="true">
                        <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"></path>
                        <circle cx="12" cy="10" r="3"></circle>
                      </svg>
                      <span>{t.receptionLocation}</span>
                    </span>

                    <span className="flex items-center gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 shrink-0" aria-hidden="true">
                        <path d="M12 6v6l4 2"></path>
                        <circle cx="12" cy="12" r="10"></circle>
                      </svg>
                      <span>{t.receptionTime}</span>
                    </span>

                    <span className="flex items-center gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 shrink-0">
                        <path d="M8 3v3M16 3v3M4 11h16M5 21h14a2 2 0 0 0 2-2V7H3v12a2 2 0 0 0 2 2z" />
                      </svg>
                      <span>{t.receptionSchedule}</span>
                    </span>
                  </div>

                  <div className="mt-2"></div>
                </div>

                <div className="mt-5 md:mt-6">
                  <Button asChild variant="outline" className="rounded-none border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors duration-300">
                    <a href="https://maps.app.goo.gl/yj6gNT9kELZonqdw6" target="_blank" rel="noopener noreferrer" aria-label={t.receptionButtonAria}>
                      {t.receptionButton}
                    </a>
                  </Button>
                </div>
              </div>
            </div>

            <div className="order-2 lg:hidden mb-8">
              <div className="aspect-[4/5] relative w-full max-w-[300px] sm:max-w-[360px] mx-auto">
                <img src="https://saraandmichael2026.com/wp-content/uploads/2026/04/ChatGPT-Image-Apr-20-2026-08_24_46-PM.png" alt={t.receptionAlt} className="w-full h-full object-cover object-center relative z-10 shadow-2xl shadow-shadow-color/20" />
              </div>
            </div>

            <div className="order-3 prose prose-lg max-w-none">
              <p className="text-muted-foreground text-left md:text-justify leading-loose border-l border-border/50 pl-4 md:pl-6 lg:border-none lg:pl-0">
                {t.receptionDescription}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}